// ---------------------------------------------------------------------------
// CALIFORNIA LOCATION HIERARCHY — single source of truth.
//
//   California  ->  County  ->  City
//
// Used by county pages, city pages, money pages, the location hub, internal
// linking and the dynamic sitemap. Never re-declare these lists in a component.
// ---------------------------------------------------------------------------

export interface County {
  /** URL slug: /en/california/counties/riverside-county */
  slug: string;
  /** Display name without the word "County". */
  name: string;
  /** Largest / most relevant metro label used in copy. */
  region: string;
  /** Rough population band used for AEO answers (no precision claimed). */
  pop: string;
}

export const STATE = { code: 'CA', name: 'California', slug: 'california' };

export const COUNTIES: County[] = [
  { slug: 'alameda-county', name: 'Alameda', region: 'Bay Area', pop: '1.6M' },
  { slug: 'alpine-county', name: 'Alpine', region: 'Sierra Nevada', pop: '1.2K' },
  { slug: 'amador-county', name: 'Amador', region: 'Gold Country', pop: '41K' },
  { slug: 'butte-county', name: 'Butte', region: 'Northern Sacramento Valley', pop: '210K' },
  { slug: 'calaveras-county', name: 'Calaveras', region: 'Gold Country', pop: '45K' },
  { slug: 'colusa-county', name: 'Colusa', region: 'Sacramento Valley', pop: '22K' },
  { slug: 'contra-costa-county', name: 'Contra Costa', region: 'Bay Area', pop: '1.1M' },
  { slug: 'del-norte-county', name: 'Del Norte', region: 'North Coast', pop: '28K' },
  { slug: 'el-dorado-county', name: 'El Dorado', region: 'Greater Sacramento', pop: '192K' },
  { slug: 'fresno-county', name: 'Fresno', region: 'Central Valley', pop: '1.0M' },
  { slug: 'glenn-county', name: 'Glenn', region: 'Sacramento Valley', pop: '28K' },
  { slug: 'humboldt-county', name: 'Humboldt', region: 'North Coast', pop: '135K' },
  { slug: 'imperial-county', name: 'Imperial', region: 'Imperial Valley', pop: '179K' },
  { slug: 'inyo-county', name: 'Inyo', region: 'Eastern Sierra', pop: '19K' },
  { slug: 'kern-county', name: 'Kern', region: 'Southern Central Valley', pop: '917K' },
  { slug: 'kings-county', name: 'Kings', region: 'Central Valley', pop: '152K' },
  { slug: 'lake-county', name: 'Lake', region: 'North Coast', pop: '68K' },
  { slug: 'lassen-county', name: 'Lassen', region: 'Northeast California', pop: '29K' },
  { slug: 'los-angeles-county', name: 'Los Angeles', region: 'Greater Los Angeles', pop: '9.7M' },
  { slug: 'madera-county', name: 'Madera', region: 'Central Valley', pop: '160K' },
  { slug: 'marin-county', name: 'Marin', region: 'Bay Area', pop: '256K' },
  { slug: 'mariposa-county', name: 'Mariposa', region: 'Sierra Nevada', pop: '17K' },
  { slug: 'mendocino-county', name: 'Mendocino', region: 'North Coast', pop: '90K' },
  { slug: 'merced-county', name: 'Merced', region: 'Central Valley', pop: '287K' },
  { slug: 'modoc-county', name: 'Modoc', region: 'Northeast California', pop: '8.7K' },
  { slug: 'mono-county', name: 'Mono', region: 'Eastern Sierra', pop: '13K' },
  { slug: 'monterey-county', name: 'Monterey', region: 'Central Coast', pop: '436K' },
  { slug: 'napa-county', name: 'Napa', region: 'Wine Country', pop: '136K' },
  { slug: 'nevada-county', name: 'Nevada', region: 'Gold Country', pop: '102K' },
  { slug: 'orange-county', name: 'Orange', region: 'Greater Los Angeles', pop: '3.2M' },
  { slug: 'placer-county', name: 'Placer', region: 'Greater Sacramento', pop: '412K' },
  { slug: 'plumas-county', name: 'Plumas', region: 'Northern Sierra', pop: '19K' },
  { slug: 'riverside-county', name: 'Riverside', region: 'Inland Empire', pop: '2.4M' },
  { slug: 'sacramento-county', name: 'Sacramento', region: 'Greater Sacramento', pop: '1.6M' },
  { slug: 'san-benito-county', name: 'San Benito', region: 'Central Coast', pop: '65K' },
  { slug: 'san-bernardino-county', name: 'San Bernardino', region: 'Inland Empire', pop: '2.2M' },
  { slug: 'san-diego-county', name: 'San Diego', region: 'San Diego Metro', pop: '3.3M' },
  { slug: 'san-francisco-county', name: 'San Francisco', region: 'Bay Area', pop: '842K' },
  { slug: 'san-joaquin-county', name: 'San Joaquin', region: 'Central Valley', pop: '789K' },
  { slug: 'san-luis-obispo-county', name: 'San Luis Obispo', region: 'Central Coast', pop: '283K' },
  { slug: 'san-mateo-county', name: 'San Mateo', region: 'Bay Area', pop: '744K' },
  { slug: 'santa-barbara-county', name: 'Santa Barbara', region: 'Central Coast', pop: '444K' },
  { slug: 'santa-clara-county', name: 'Santa Clara', region: 'Silicon Valley', pop: '1.9M' },
  { slug: 'santa-cruz-county', name: 'Santa Cruz', region: 'Central Coast', pop: '264K' },
  { slug: 'shasta-county', name: 'Shasta', region: 'Northern California', pop: '181K' },
  { slug: 'sierra-county', name: 'Sierra', region: 'Northern Sierra', pop: '3.2K' },
  { slug: 'siskiyou-county', name: 'Siskiyou', region: 'Northern California', pop: '44K' },
  { slug: 'solano-county', name: 'Solano', region: 'Bay Area', pop: '453K' },
  { slug: 'sonoma-county', name: 'Sonoma', region: 'Wine Country', pop: '482K' },
  { slug: 'stanislaus-county', name: 'Stanislaus', region: 'Central Valley', pop: '552K' },
  { slug: 'sutter-county', name: 'Sutter', region: 'Sacramento Valley', pop: '99K' },
  { slug: 'tehama-county', name: 'Tehama', region: 'Sacramento Valley', pop: '65K' },
  { slug: 'trinity-county', name: 'Trinity', region: 'Northern California', pop: '16K' },
  { slug: 'tulare-county', name: 'Tulare', region: 'Central Valley', pop: '477K' },
  { slug: 'tuolumne-county', name: 'Tuolumne', region: 'Sierra Nevada', pop: '55K' },
  { slug: 'ventura-county', name: 'Ventura', region: 'Southern California Coast', pop: '839K' },
  { slug: 'yolo-county', name: 'Yolo', region: 'Greater Sacramento', pop: '220K' },
  { slug: 'yuba-county', name: 'Yuba', region: 'Sacramento Valley', pop: '84K' },
];

export interface City {
  /** URL slug: /en/california/riverside */
  slug: string;
  name: string;
  /** County slug this city belongs to. */
  county: string;
}

const c = (name: string, county: string): City => ({
  slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
  name,
  county,
});

export const CITIES: City[] = [
  // ---- Inland Empire (Riverside) ----
  c('Riverside', 'riverside-county'), c('Corona', 'riverside-county'), c('Moreno Valley', 'riverside-county'),
  c('Temecula', 'riverside-county'), c('Murrieta', 'riverside-county'), c('Menifee', 'riverside-county'),
  c('Hemet', 'riverside-county'), c('Perris', 'riverside-county'), c('Lake Elsinore', 'riverside-county'),
  c('Jurupa Valley', 'riverside-county'), c('Eastvale', 'riverside-county'), c('Norco', 'riverside-county'),
  c('Beaumont', 'riverside-county'), c('Banning', 'riverside-county'), c('San Jacinto', 'riverside-county'),
  c('Wildomar', 'riverside-county'), c('Palm Springs', 'riverside-county'), c('Palm Desert', 'riverside-county'),
  c('La Quinta', 'riverside-county'), c('Indio', 'riverside-county'), c('Cathedral City', 'riverside-county'),
  c('Coachella', 'riverside-county'), c('Rancho Mirage', 'riverside-county'), c('Desert Hot Springs', 'riverside-county'),
  c('Canyon Lake', 'riverside-county'), c('Calimesa', 'riverside-county'),
  // ---- Inland Empire (San Bernardino) ----
  c('San Bernardino', 'san-bernardino-county'), c('Fontana', 'san-bernardino-county'), c('Ontario', 'san-bernardino-county'),
  c('Rancho Cucamonga', 'san-bernardino-county'), c('Rialto', 'san-bernardino-county'), c('Upland', 'san-bernardino-county'),
  c('Redlands', 'san-bernardino-county'), c('Chino', 'san-bernardino-county'), c('Chino Hills', 'san-bernardino-county'),
  c('Victorville', 'san-bernardino-county'), c('Hesperia', 'san-bernardino-county'), c('Apple Valley', 'san-bernardino-county'),
  c('Colton', 'san-bernardino-county'), c('Yucaipa', 'san-bernardino-county'), c('Montclair', 'san-bernardino-county'),
  c('Highland', 'san-bernardino-county'), c('Loma Linda', 'san-bernardino-county'), c('Barstow', 'san-bernardino-county'),
  c('Adelanto', 'san-bernardino-county'), c('Twentynine Palms', 'san-bernardino-county'), c('Yucca Valley', 'san-bernardino-county'),
  c('Grand Terrace', 'san-bernardino-county'),
  // ---- Los Angeles County ----
  c('Los Angeles', 'los-angeles-county'), c('Long Beach', 'los-angeles-county'), c('Glendale', 'los-angeles-county'),
  c('Santa Clarita', 'los-angeles-county'), c('Lancaster', 'los-angeles-county'), c('Palmdale', 'los-angeles-county'),
  c('Pomona', 'los-angeles-county'), c('Torrance', 'los-angeles-county'), c('Pasadena', 'los-angeles-county'),
  c('El Monte', 'los-angeles-county'), c('Downey', 'los-angeles-county'), c('Inglewood', 'los-angeles-county'),
  c('West Covina', 'los-angeles-county'), c('Norwalk', 'los-angeles-county'), c('Burbank', 'los-angeles-county'),
  c('Compton', 'los-angeles-county'), c('South Gate', 'los-angeles-county'), c('Carson', 'los-angeles-county'),
  c('Santa Monica', 'los-angeles-county'), c('Whittier', 'los-angeles-county'), c('Hawthorne', 'los-angeles-county'),
  c('Alhambra', 'los-angeles-county'), c('Lakewood', 'los-angeles-county'), c('Bellflower', 'los-angeles-county'),
  c('Baldwin Park', 'los-angeles-county'), c('Lynwood', 'los-angeles-county'), c('Redondo Beach', 'los-angeles-county'),
  c('Glendora', 'los-angeles-county'), c('Covina', 'los-angeles-county'), c('Azusa', 'los-angeles-county'),
  c('Monrovia', 'los-angeles-county'), c('Arcadia', 'los-angeles-county'), c('Diamond Bar', 'los-angeles-county'),
  c('Walnut', 'los-angeles-county'), c('La Verne', 'los-angeles-county'), c('San Dimas', 'los-angeles-county'),
  c('Claremont', 'los-angeles-county'), c('Culver City', 'los-angeles-county'), c('Manhattan Beach', 'los-angeles-county'),
  c('El Segundo', 'los-angeles-county'), c('Montebello', 'los-angeles-county'), c('Pico Rivera', 'los-angeles-county'),
  c('Rosemead', 'los-angeles-county'), c('Cerritos', 'los-angeles-county'), c('Paramount', 'los-angeles-county'),
  // ---- Orange County ----
  c('Anaheim', 'orange-county'), c('Santa Ana', 'orange-county'), c('Irvine', 'orange-county'),
  c('Huntington Beach', 'orange-county'), c('Garden Grove', 'orange-county'), c('Orange', 'orange-county'),
  c('Fullerton', 'orange-county'), c('Costa Mesa', 'orange-county'), c('Mission Viejo', 'orange-county'),
  c('Westminster', 'orange-county'), c('Newport Beach', 'orange-county'), c('Buena Park', 'orange-county'),
  c('Lake Forest', 'orange-county'), c('Tustin', 'orange-county'), c('Yorba Linda', 'orange-county'),
  c('San Clemente', 'orange-county'), c('Laguna Niguel', 'orange-county'), c('La Habra', 'orange-county'),
  c('Fountain Valley', 'orange-county'), c('Placentia', 'orange-county'), c('Rancho Santa Margarita', 'orange-county'),
  c('Aliso Viejo', 'orange-county'), c('Cypress', 'orange-county'), c('Brea', 'orange-county'),
  c('Stanton', 'orange-county'), c('San Juan Capistrano', 'orange-county'), c('Dana Point', 'orange-county'),
  c('Laguna Beach', 'orange-county'), c('Seal Beach', 'orange-county'),
  // ---- San Diego County ----
  c('San Diego', 'san-diego-county'), c('Chula Vista', 'san-diego-county'), c('Oceanside', 'san-diego-county'),
  c('Escondido', 'san-diego-county'), c('Carlsbad', 'san-diego-county'), c('El Cajon', 'san-diego-county'),
  c('Vista', 'san-diego-county'), c('San Marcos', 'san-diego-county'), c('Encinitas', 'san-diego-county'),
  c('National City', 'san-diego-county'), c('La Mesa', 'san-diego-county'), c('Santee', 'san-diego-county'),
  c('Poway', 'san-diego-county'), c('Chula Vista East', 'san-diego-county'), c('Imperial Beach', 'san-diego-county'),
  // ---- Central Valley ----
  c('Fresno', 'fresno-county'), c('Clovis', 'fresno-county'), c('Selma', 'fresno-county'), c('Reedley', 'fresno-county'),
  c('Bakersfield', 'kern-county'), c('Delano', 'kern-county'), c('Ridgecrest', 'kern-county'), c('Tehachapi', 'kern-county'),
  c('Visalia', 'tulare-county'), c('Tulare', 'tulare-county'), c('Porterville', 'tulare-county'),
  c('Modesto', 'stanislaus-county'), c('Turlock', 'stanislaus-county'), c('Ceres', 'stanislaus-county'),
  c('Stockton', 'san-joaquin-county'), c('Tracy', 'san-joaquin-county'), c('Manteca', 'san-joaquin-county'),
  c('Lodi', 'san-joaquin-county'), c('Merced', 'merced-county'), c('Los Banos', 'merced-county'),
  c('Madera', 'madera-county'), c('Hanford', 'kings-county'), c('Lemoore', 'kings-county'),
  // ---- Greater Sacramento ----
  c('Sacramento', 'sacramento-county'), c('Elk Grove', 'sacramento-county'), c('Citrus Heights', 'sacramento-county'),
  c('Folsom', 'sacramento-county'), c('Rancho Cordova', 'sacramento-county'), c('Galt', 'sacramento-county'),
  c('Roseville', 'placer-county'), c('Rocklin', 'placer-county'), c('Lincoln', 'placer-county'),
  c('Auburn', 'placer-county'), c('El Dorado Hills', 'el-dorado-county'), c('Placerville', 'el-dorado-county'),
  c('Davis', 'yolo-county'), c('Woodland', 'yolo-county'), c('West Sacramento', 'yolo-county'),
  c('Yuba City', 'sutter-county'), c('Marysville', 'yuba-county'),
  // ---- Bay Area ----
  c('San Jose', 'santa-clara-county'), c('Sunnyvale', 'santa-clara-county'), c('Santa Clara', 'santa-clara-county'),
  c('Mountain View', 'santa-clara-county'), c('Palo Alto', 'santa-clara-county'), c('Milpitas', 'santa-clara-county'),
  c('Gilroy', 'santa-clara-county'), c('Morgan Hill', 'santa-clara-county'), c('Cupertino', 'santa-clara-county'),
  c('Oakland', 'alameda-county'), c('Fremont', 'alameda-county'), c('Hayward', 'alameda-county'),
  c('Berkeley', 'alameda-county'), c('San Leandro', 'alameda-county'), c('Livermore', 'alameda-county'),
  c('Pleasanton', 'alameda-county'), c('Dublin', 'alameda-county'), c('Union City', 'alameda-county'),
  c('Newark', 'alameda-county'), c('Alameda', 'alameda-county'),
  c('San Francisco', 'san-francisco-county'),
  c('Concord', 'contra-costa-county'), c('Richmond', 'contra-costa-county'), c('Antioch', 'contra-costa-county'),
  c('Walnut Creek', 'contra-costa-county'), c('Brentwood', 'contra-costa-county'), c('Pittsburg', 'contra-costa-county'),
  c('San Ramon', 'contra-costa-county'), c('Danville', 'contra-costa-county'),
  c('Daly City', 'san-mateo-county'), c('San Mateo', 'san-mateo-county'), c('Redwood City', 'san-mateo-county'),
  c('South San Francisco', 'san-mateo-county'), c('San Bruno', 'san-mateo-county'), c('Menlo Park', 'san-mateo-county'),
  c('Vallejo', 'solano-county'), c('Fairfield', 'solano-county'), c('Vacaville', 'solano-county'),
  c('Benicia', 'solano-county'), c('Suisun City', 'solano-county'),
  c('Santa Rosa', 'sonoma-county'), c('Petaluma', 'sonoma-county'), c('Rohnert Park', 'sonoma-county'),
  c('Windsor', 'sonoma-county'), c('Napa', 'napa-county'), c('American Canyon', 'napa-county'),
  c('San Rafael', 'marin-county'), c('Novato', 'marin-county'), c('Mill Valley', 'marin-county'),
  // ---- Central Coast ----
  c('Oxnard', 'ventura-county'), c('Thousand Oaks', 'ventura-county'), c('Simi Valley', 'ventura-county'),
  c('Ventura', 'ventura-county'), c('Camarillo', 'ventura-county'), c('Santa Paula', 'ventura-county'),
  c('Santa Barbara', 'santa-barbara-county'), c('Santa Maria', 'santa-barbara-county'), c('Lompoc', 'santa-barbara-county'),
  c('Goleta', 'santa-barbara-county'), c('San Luis Obispo', 'san-luis-obispo-county'), c('Paso Robles', 'san-luis-obispo-county'),
  c('Atascadero', 'san-luis-obispo-county'), c('Arroyo Grande', 'san-luis-obispo-county'),
  c('Salinas', 'monterey-county'), c('Monterey', 'monterey-county'), c('Seaside', 'monterey-county'),
  c('Marina', 'monterey-county'), c('Santa Cruz', 'santa-cruz-county'), c('Watsonville', 'santa-cruz-county'),
  c('Hollister', 'san-benito-county'),
  // ---- Northern California ----
  c('Chico', 'butte-county'), c('Paradise', 'butte-county'), c('Oroville', 'butte-county'),
  c('Redding', 'shasta-county'), c('Anderson', 'shasta-county'), c('Eureka', 'humboldt-county'),
  c('Arcata', 'humboldt-county'), c('Ukiah', 'mendocino-county'), c('Clearlake', 'lake-county'),
  c('Red Bluff', 'tehama-county'), c('Susanville', 'lassen-county'), c('Yreka', 'siskiyou-county'),
  c('Crescent City', 'del-norte-county'), c('Grass Valley', 'nevada-county'), c('Truckee', 'nevada-county'),
  c('Jackson', 'amador-county'), c('Sonora', 'tuolumne-county'), c('Angels Camp', 'calaveras-county'),
  c('Colusa', 'colusa-county'), c('Willows', 'glenn-county'), c('Weaverville', 'trinity-county'),
  c('Alturas', 'modoc-county'), c('Quincy', 'plumas-county'), c('Loyalton', 'sierra-county'),
  c('Mammoth Lakes', 'mono-county'), c('Bishop', 'inyo-county'), c('Markleeville', 'alpine-county'),
  c('Mariposa', 'mariposa-county'),
  // ---- Imperial Valley ----
  c('El Centro', 'imperial-county'), c('Calexico', 'imperial-county'), c('Brawley', 'imperial-county'),
  c('Imperial', 'imperial-county'),
];

export const countyBySlug = (slug: string) => COUNTIES.find((x) => x.slug === slug);
export const cityBySlug = (slug: string) => CITIES.find((x) => x.slug === slug);
export const citiesInCounty = (countySlug: string) => CITIES.filter((x) => x.county === countySlug);

/** Nearby cities = other cities in the same county (capped for link hygiene). */
export const nearbyCities = (city: City, max = 12) =>
  CITIES.filter((x) => x.county === city.county && x.slug !== city.slug).slice(0, max);

/** The metros that get dedicated money pages. */
export const TOP_METRO_SLUGS = [
  'riverside', 'san-bernardino', 'fontana', 'ontario', 'rancho-cucamonga', 'corona',
  'moreno-valley', 'temecula', 'los-angeles', 'anaheim', 'san-diego', 'long-beach',
  'irvine', 'bakersfield', 'fresno', 'sacramento', 'san-jose', 'oakland',
];

export const TOP_COUNTY_SLUGS = [
  'riverside-county', 'san-bernardino-county', 'orange-county', 'los-angeles-county',
  'san-diego-county', 'kern-county', 'sacramento-county', 'ventura-county',
];
